import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LayoutDashboard, LogOut, ShieldCheck, ChevronDown, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { SERVICES } from "@/data/services";

const ThemeToggle = ({ className = "" }) => {
  const { theme, toggle } = useTheme();
  return (
    <button onClick={toggle} aria-label="Toggle theme" data-testid="theme-toggle"
      className={`w-10 h-10 rounded-full flex items-center justify-center text-brand-navy hover:bg-slate-100 transition-colors ${className}`}>
      {theme === "dark" ? <Sun className="w-5 h-5 text-brand-gold" /> : <Moon className="w-5 h-5" />}
    </button>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => { setOpen(false); setSvcOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dashPath = user && user.role === "admin" ? "/admin" : "/dashboard";
  const linkCls = (to) => `px-4 py-2 rounded-full text-sm font-medium transition-colors ${loc.pathname === to ? "text-brand-red" : "text-slate-700 hover:text-brand-red"}`;

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-brand-red text-white text-[11px] sm:text-xs py-2 px-4 text-center font-medium tracking-wide" data-testid="disclaimer-banner">
        <ShieldCheck className="inline-block w-3.5 h-3.5 mr-1.5 -mt-0.5" />
        Disclaimer: This is not an official Vietnam government website. ImmiLaw.VN is a private immigration &amp; legal consultancy.
      </div>

      <div className={`glass border-b transition-colors ${scrolled ? "border-slate-200 shadow-soft" : "border-transparent"}`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img src="/logo.png" alt="ImmiLaw.VN" className="h-11 w-auto transition-transform group-hover:scale-105 dark:hidden" />
            <img src="/logo.png" alt="ImmiLaw.VN" className="h-9 w-auto rounded-md bg-white p-0.5 transition-transform group-hover:scale-105 hidden dark:block" />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link to="/" className={linkCls("/")} data-testid="nav-home">Home</Link>
            <div className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
              <Link to="/services" className={`${linkCls("/services")} inline-flex items-center gap-1`} data-testid="nav-services">
                Services <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              <AnimatePresence>
                {svcOpen && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                    className="absolute left-0 top-full pt-2 w-72" data-testid="services-dropdown">
                    <div className="bg-white rounded-2xl shadow-lift border border-slate-200 p-2">
                      {SERVICES.map((s) => (
                        <Link key={s.key} to={`/services/${s.slug}`} data-testid={`nav-service-${s.key}`}
                          className="block px-4 py-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                          <span className="text-sm font-semibold text-brand-navy block">{s.title}</span>
                          <span className="text-xs text-slate-500 line-clamp-1">{s.short}</span>
                        </Link>
                      ))}
                      <Link to="/services" className="block px-4 py-2.5 text-sm font-semibold text-brand-red hover:underline">View all services →</Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/contact" className={linkCls("/contact")} data-testid="nav-contact">Contact</Link>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <>
                <div className="flex items-center gap-2 pl-1" data-testid="header-user">
                  {user.picture ? (
                    <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-brand-navy text-white text-xs font-bold flex items-center justify-center">{(user.name || user.email || "U").charAt(0).toUpperCase()}</div>
                  )}
                  <span className="text-sm font-semibold text-brand-navy max-w-[130px] truncate">{user.name || user.email}</span>
                </div>
                <Link to={dashPath} data-testid="header-dashboard-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-brand-navy hover:bg-slate-100 transition-colors">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                <button onClick={() => { logout(); nav("/"); }} data-testid="header-logout-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:text-brand-red transition-colors">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-btn" className="px-4 py-2 rounded-full text-sm font-semibold text-brand-navy hover:bg-slate-100 transition-colors">Login</Link>
                <Link to="/register" data-testid="header-register-btn" className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-brand-red hover:bg-brand-reddark transition-colors shadow-redglow">Get Started</Link>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center gap-1">
            <ThemeToggle />
            <button className="p-2 text-brand-navy" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden bg-white border-t border-slate-200">
              <div className="px-4 py-4 flex flex-col gap-1">
                <Link to="/" className="px-3 py-3 rounded-lg text-slate-800 font-medium hover:bg-slate-50">Home</Link>
                <span className="px-3 pt-2 text-xs uppercase tracking-widest text-slate-400 font-bold">Services</span>
                {SERVICES.map((s) => (
                  <Link key={s.key} to={`/services/${s.slug}`} className="px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-50 text-sm">{s.title}</Link>
                ))}
                <Link to="/contact" className="px-3 py-3 rounded-lg text-slate-800 font-medium hover:bg-slate-50">Contact</Link>
                <div className="h-px bg-slate-200 my-2" />
                {user ? (
                  <>
                    <Link to={dashPath} className="px-3 py-3 rounded-lg font-semibold text-brand-navy hover:bg-slate-50">Dashboard</Link>
                    <button onClick={() => { logout(); nav("/"); }} className="text-left px-3 py-3 rounded-lg font-semibold text-brand-red hover:bg-slate-50">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="px-3 py-3 rounded-lg font-semibold text-brand-navy hover:bg-slate-50">Login</Link>
                    <Link to="/register" className="px-3 py-3 rounded-lg font-semibold text-white bg-brand-red text-center">Get Started</Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
